import { Text } from "@chakra-ui/react";
import { formatDate } from "@syla/shared/helpers/formatting";
import { assertNever } from "@syla/shared/types/helpers/errors";
import { SubscriptionWithPlansAndAccess } from "@syla/shared/types/responses/GetSubscriptionsResponse";
import { getSubscriptionState } from "@syla/shared/types/subscriptions/getSubscriptionState";
import {
  RenewalAction,
  Lifecycle,
} from "@syla/shared/types/subscriptions/subscriptionState";
import React from "react";
import { SettingRowBase } from "../components/SettingRowBase";

interface PlanRenewalDateProps {
  subscription?: SubscriptionWithPlansAndAccess;
}

export const PlanRenewalDate = ({
  subscription,
}: PlanRenewalDateProps): React.ReactElement | null => {
  if (!subscription) {
    return null;
  }

  const state = getSubscriptionState(subscription);

  const renewal = state.renewal;

  // It's hard to make the end date make sense when the plan has terminated, as
  // it can be caused by different reasons.
  // e.g. what end date would you show if the plan was cancelled and refunded?
  // It's much simpler to just not show this field if the plan has terminated.
  if (
    state.lifecycle === Lifecycle.Terminated ||
    renewal === RenewalAction.None
    // renewal is always None if the lifecycle is Terminated, we include both
    // to type narrow the field.
  ) {
    return null;
  }

  const info = getRenewalInfo(renewal);

  const date = formatDate(state.periodEnd);

  return (
    <SettingRowBase label={info.header} description={info.description}>
      <Text>{date}</Text>
    </SettingRowBase>
  );
};

function getRenewalInfo(renewal: Exclude<RenewalAction, RenewalAction.None>): {
  header: string;
  description: string;
} {
  if (renewal === RenewalAction.Cancel) {
    return {
      header: "Plan end date",
      description: "Your plan will expire on this date.",
    };
  }

  if (renewal === RenewalAction.Downgrade) {
    return {
      header: "Plan renewal date",
      description:
        "Your plan will automatically downgrade and renew on this date for another year.",
    };
  }

  if (renewal === RenewalAction.Renew) {
    return {
      header: "Plan renewal date",
      description:
        "Your plan will automatically renew on this date for another year.",
    };
  }

  assertNever(renewal);
}
